import { defineStore } from 'pinia';
import { ref } from 'vue';
import { sportsBookList as list } from '@/shared/SportsBook';
import type { SportsBook, SportsBookSlug } from '@/types/SportsBook';

const bscode = ref<string | null>(null);

function setBscode(value: string | null) {
  bscode.value = value;
}

function clearBscode() {
  bscode.value = null;
}

function hasSomeValidOption(options?: Record<string, any>): boolean {
  if (!options) {
    return false;
  }

  const entryValues = Object.values(options);

  return !!entryValues.length && entryValues.every((entry) => !!entry);
}

export const useSportsBookStore = defineStore('sportsBook', () => {
  const { isBetByEnabled, isAltenarEnabled, isCactusSportsbookEnabled } =
    useFeatureFlagsEnv();

  const sportsBookList = computed<SportsBook[]>(() => {
    return list;
  });

  const menuItems = computed<SportsBook[]>(() => {
    return sportsBookList.value.reduce<SportsBook[]>((total, current) => {
      const isEntryValid = isMenuItemEntryValid(current);

      if (isEntryValid) {
        return [...total, current];
      }

      return total;
    }, []);
  });

  function isMenuItemEntryValid(data: SportsBook): boolean {
    if (isCactusSportsbookEnabled.value) {
      return hasSomeValidOption(data.path.cactus);
    }

    if (isAltenarEnabled.value) {
      return !!data.path.altenar;
    }

    if (isBetByEnabled.value) {
      return !!data.path.betby;
    }

    return false;
  }

  function isSportBookPathValid(sportBookSlug: SportsBookSlug): boolean {
    return sportsBookList.value.some(
      (sportBook) => sportBook.slug === sportBookSlug,
    );
  }

  function getSportBookData(sportBookSlug: SportsBookSlug): SportsBook {
    const hasSportBookPath = isSportBookPathValid(sportBookSlug);

    if (!hasSportBookPath) {
      throw new Error('sportsbook: an invalid slug was used');
    }

    const sportBookData = sportsBookList.value.find(
      (sportBook) => sportBook.slug === sportBookSlug,
    ) as NonNullable<SportsBook>;

    return sportBookData;
  }

  return {
    menuItems,
    isSportBookPathValid,
    getSportBookData,
    setBscode,
    bscode,
    clearBscode,
  };
});
